import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Clock,
  Banknote,
  ArrowRight,
  Mail,
  Users,
  Rocket,
  Heart,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';

const openRoles = [
  {
    title: 'Technical Sales Lead',
    type: 'Full-time or Contract',
    location: 'Remote',
    salary: 'Competitive + equity',
    description:
      'Medico-legal case preparation is slow, manual, and overdue for change. We\'re looking for someone who believes in that mission — and can confidently show solicitors, barristers, and medical experts how ClinLexis helps them deliver better outcomes, faster. You\'ll own the go-to-market strategy and the full sales cycle from first conversation to close.',
    responsibilities: [
      'Lead product demos and discovery calls with prospective customers across the legal and medical sectors',
      'Develop and execute a repeatable go-to-market strategy for ClinLexis',
      'Build relationships with law firms, chambers, and medical agencies',
      'Work closely with the founders to shape pricing, positioning, and messaging',
      'Gather customer feedback and feed it directly into product development',
      'Represent ClinLexis at industry events, conferences, and webinars',
    ],
    requirements: [
      'Experience selling SaaS or technology products, ideally into legal or healthcare',
      'Comfortable giving live product demos and explaining technical concepts in plain language',
      'Self-starter who thrives in an early-stage environment with lots of autonomy',
      'Excellent written and verbal communication skills',
      'Passionate about delivering genuine value to customers through technology',
    ],
    niceToHave: [
      'Existing network in the legal or medical expert witness space',
      'Experience with NHS procurement or compliance requirements',
      'Familiarity with AI-powered products and how to position them',
    ],
  },
];

export const JobsPage: React.FC = () => {
  useForceLightMode();

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
            Join ClinLexis
          </h1>
          <p className="text-xl text-medical-600 leading-relaxed font-light max-w-2xl mx-auto">
            Medical negligence, personal injury, and clinical disputes affect real people at the most
            difficult times in their lives. We're building the tools that help the professionals on
            those cases work faster, more accurately, and with greater confidence.
          </p>
        </div>

        {/* Why join us */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: Rocket,
              title: 'Early stage, big impact',
              description: 'Help shape the direction of a company that\'s solving real problems for real professionals.',
            },
            {
              icon: Heart,
              title: 'Mission-driven',
              description: 'Better tools for medico-legal professionals mean better outcomes for the patients and families who depend on them.',
            },
            {
              icon: Users,
              title: 'Experienced founders',
              description: 'Work alongside an NHS consultant neurologist and an engineering leader with two decades of experience in regulated industries.',
            },
          ].map((item, index) => (
            <div key={index} className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-medical-200/50 text-center">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <item.icon className="h-5 w-5 text-primary-600" />
              </div>
              <h3 className="font-semibold text-medical-900 mb-2">{item.title}</h3>
              <p className="text-sm text-medical-600">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Open roles */}
        <h2 className="text-2xl font-bold text-medical-900 mb-8">Open roles</h2>

        {openRoles.map((role, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg border border-medical-200/50 p-8 md:p-10 mb-10">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-medical-900 mb-3">{role.title}</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: Clock, text: role.type },
                  { icon: MapPin, text: role.location },
                  { icon: Banknote, text: role.salary },
                ].map((tag, i) => (
                  <span key={i} className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-medical-50 border border-medical-200 rounded-full text-xs font-medium text-medical-700">
                    <tag.icon className="h-3 w-3" />
                    <span>{tag.text}</span>
                  </span>
                ))}
              </div>
            </div>

            <p className="text-medical-600 leading-relaxed mb-8">{role.description}</p>

            <div className="mb-8">
              <h4 className="font-semibold text-medical-900 mb-3">What you'll do</h4>
              <ul className="space-y-2">
                {role.responsibilities.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-medical-600">
                    <span className="text-primary-600 mt-1">&#8226;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-8">
              <h4 className="font-semibold text-medical-900 mb-3">What we're looking for</h4>
              <ul className="space-y-2">
                {role.requirements.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-medical-600">
                    <span className="text-primary-600 mt-1">&#8226;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-8">
              <h4 className="font-semibold text-medical-900 mb-3">Nice to have</h4>
              <ul className="space-y-2">
                {role.niceToHave.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-medical-600">
                    <span className="text-medical-400 mt-1">&#8226;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href="mailto:contact@clinlexis.co.uk?subject=Application%20—%20Technical%20Sales%20Lead"
              className="btn-primary px-8 py-3.5 text-base inline-flex items-center space-x-2 group shadow-lg shadow-primary-200/50 hover:shadow-xl transition-all"
            >
              <Mail className="h-5 w-5" />
              <span>Apply now</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        ))}

        {/* Don't see a role */}
        <div className="bg-medical-50 rounded-2xl p-8 text-center mb-16">
          <h3 className="text-lg font-semibold text-medical-900 mb-2">Don't see the right role?</h3>
          <p className="text-medical-600 mb-4 text-sm">
            We're always interested in hearing from talented people. Send us a note
            and tell us how you'd like to contribute.
          </p>
          <a
            href="mailto:contact@clinlexis.co.uk?subject=General%20enquiry%20—%20Careers"
            className="text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors inline-flex items-center space-x-1 group"
          >
            <span>contact@clinlexis.co.uk</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Equal opportunities */}
        <div className="border-t border-medical-200 pt-10">
          <h3 className="text-lg font-semibold text-medical-900 mb-3">Equal opportunities employer</h3>
          <p className="text-sm text-medical-600 leading-relaxed mb-4">
            ClinLexis Ltd is an equal opportunities employer. We are committed to creating an inclusive
            environment for all employees and candidates. We welcome applications from people of all
            backgrounds, regardless of age, disability, gender reassignment, marriage and civil
            partnership, pregnancy and maternity, race, religion or belief, sex, or sexual orientation.
          </p>
          <p className="text-sm text-medical-600 leading-relaxed">
            We believe diverse teams build better products. If you require any reasonable adjustments
            during the application process, please let us know and we'll do our best to accommodate you.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};
